/*!
  Pioneer WFRIA ProgressStepper v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["ProgressStepper"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("ProgressStepper") < 0) {
    window.__PIONEER_GLOBAL__.components.push("ProgressStepper");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "customStrings", "orientation"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React from "react";
import { validChild } from "../common/proptypes";
import Step from "../Step/Step";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
/**
 * ProgressStepper is a React component that informs users which
 * step of a multi-step process they are currently on, which steps
 * have been completed, and which steps remain.
 *
 * This is a controlled component.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";

function ProgressStepper(_ref) {
  let {
    children,
    customStrings,
    orientation
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot
  } = extractTheme(_objectSpread({
    theme
  }, otherProps));
  const componentStrings = customStrings === null ? STRINGS : _objectSpread(_objectSpread(_objectSpread({}, STRINGS), customStrings), {}, {
    stepStatus: _objectSpread(_objectSpread({}, STRINGS.stepStatus), customStrings.stepStatus)
  });
  /**
   * Returns an array of Step elements where each step is altered to
   * provide the current step value, the label and the default label.
   * These are used to render an accessible node comprised of a
   * combination of hidden elements used to announce the step via
   * assistive technology.
   */

  const steps = React.Children.map(children, (element, index) => {
    var _children, _children$props;

    /**
     * In practice, no step will be labeled as "Step 0".
     */
    const stepValue = index + 1;
    const prevStepStatus = index > 0 && orientation === "horizontal" ? (_children = children[index - 1]) === null || _children === void 0 ? void 0 : (_children$props = _children.props) === null || _children$props === void 0 ? void 0 : _children$props.status : "";
    /**
     * The default step label is "Step <x>"
     */

    const defaultStepLabel = `${componentStrings.defaultStepLabel} ${stepValue}`;
    return /*#__PURE__*/React.cloneElement(element, {
      defaultStepLabel,
      componentStrings,
      orientation,
      stepValue,
      prevStepStatus
    });
  });
  return _jsx("ol", _objectSpread(_objectSpread({
    className: `pnr-progress-stepper--${orientation}`,
    css: cssRoot
  }, otherProps), {}, {
    spacing: null,
    children: steps
  }));
}

ProgressStepper.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Progress Stepper accepts only [Step](#/Component%20helpers/Step) components as children.
   */
  children: validChild([Step]),

  /**
   * Override for strings used within the component.
   */
  customStrings: PropTypes.shape({
    /**
     * Permits modification of the default `stepLabel` value across all Steps (e.g. "Step `<x>`").
     */
    defaultStepLabel: PropTypes.string,

    /**
     * Override appended accessibility strings read by assistive technology:<br />
     * - `active:string` - default text is "in progress"<br/>
     * - `completed:string` - default text is "completed"<br/>
     * - `pending:string` - default text is "incomplete"
     */
    stepStatus: PropTypes.shape({
      active: PropTypes.string,
      completed: PropTypes.string,
      pending: PropTypes.string
    })
  }),

  /**
   * Direction of the Steps.
   */
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),

  /**
   * Custom spacing for the component. See the
   * "Customization -> Spacing" section.
   */
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.shape()])
} : {};
ProgressStepper.defaultProps = {
  children: null,
  customStrings: null,
  orientation: "horizontal",
  spacing: {}
};
export default ProgressStepper;